/* eslint-disable react/no-danger */
import styles from './styles.module.scss';
import { TransWithDefault } from '@starsoft/common/components/TransWithDefault';
import NotificationCoin from '@starsoft/common/components/Notification/Coin';
import { TransactionsCommonMappingModalContentDetailsActionRowProps } from './props';
import { WalletService } from '@starsoft/common/services';
import useFormattedCryptoAddress from '@starsoft/common/hooks/useFormattedCryptoAddress';
import { getMoneyOrAmount } from '@starsoft/common/utils';

/**
 * TransactionsCommonMappingModalContentDetailsActionRow Component
 * This component is responsible for rendering a row of transaction action details.
 * It displays the transfer details including the amount, sender, and receiver information.
 *
 * @param {TransactionsCommonMappingModalContentDetailsActionRowProps} props - The properties for the component.
 * @param {Object} props.transfer - The transfer object containing transaction details.
 * @param {boolean} props.isLoading - Flag indicating whether the data is still loading.
 *
 * @returns {JSX.Element} The rendered component displaying either a loading skeleton or the transaction details.
 *
 * @example
 * // Usage example:
 * <TransactionsCommonMappingModalContentDetailsActionRow
 *   transfer={{ amount: 100, coin: { symbol: 'BTC' }, sender: 'senderAddress', receiver: 'receiverAddress' }}
 *   isLoading={false}
 * />
 */
export function TransactionsCommonMappingModalContentDetailsActionRow({
  transfer,
  isLoading,
}: TransactionsCommonMappingModalContentDetailsActionRowProps) {
  // Calculate the amount using the useMoney hook

  // Format the sender and receiver addresses
  const maskedSender = useFormattedCryptoAddress(transfer?.sender);
  const maskedReceiver = useFormattedCryptoAddress(transfer?.receiver);

  // If the data is loading, render the skeleton component
  if (isLoading) {
    return (
      <div className={styles.skeleton}>
        <div className={styles.skeleton__text} />
        <div
          className={`${styles.skeleton__text} ${styles['skeleton__text--small']}`}
        />
      </div>
    );
  }

  // Render the transaction details with icons and formatted addresses
  return (
    <div className={styles.row}>
      <TransWithDefault
        i18nKey="common:transfer_action_label"
        components={{
          icon: <i className="fa-solid fa-right-left" />,
          coin: <NotificationCoin coin={transfer?.coin} />,
          from: (
            <span
              dangerouslySetInnerHTML={{
                __html: `<span>${maskedSender}</span>`,
              }}
            />
          ),
          to: (
            <span
              dangerouslySetInnerHTML={{
                __html: `<span>${maskedReceiver}</span>`,
              }}
            />
          ),
        }}
        values={{
          amount: getMoneyOrAmount(transfer?.amount),
          converted: WalletService.maskCurrency({
            amount: getMoneyOrAmount(transfer?.amount),
            coin: transfer?.coin,
            hideSymbol: true,
          }),
        }}
      />
    </div>
  );
}
